// Default export is the reducer
import update from "immutability-helper";
import _ from "lodash";

import {
  getDeletedRowKey,
  getCourseLevelKey,
  getGradeRecordKey,
  getNewCourseRatingKey,
  getCourseCodeKey,
} from "./helpers/commonUtils";

// Action Types
const NAME = "academicRecords";

export const TOGGLE_EDIT_MODE = `TOGGLE_EDIT_MODE ${NAME}`;
export const UPDATE_STUDENT_PERSONAL_DETAILS = `UPDATE_STUDENT_PERSONAL_DETAILS ${NAME}`;
export const UPDATE_STUDENT_TRANSCRIPT_COMMENT = `UPDATE_STUDENT_TRANSCRIPT_COMMENT ${NAME}`;
export const RESET_ACADEMIC_RECORDS_MODULE = `RESET_ACADEMIC_RECORDS_MODULE ${NAME}`;
export const UPDATE_STUDENT_GRADE_RECORDS = `UPDATE_STUDENT_GRADE_RECORDS ${NAME}`;
export const DELETE_RECORD_ROW = `DELETE_RECORD_ROW ${NAME}`;
export const ADD_NEW_COURSE_ROW = `ADD_NEW_COURSE_ROW ${NAME}`;
export const UPDATE_NEW_COURSE_ROW = `UPDATE_NEW_COURSE_ROW ${NAME}`;
export const DELETE_NEW_COURSE_ROW = `DELETE_NEW_COURSE_ROW ${NAME}`;
export const UPDATE_NEW_COURSE_RATING = `UPDATE_NEW_COURSE_RATING ${NAME}`;
export const UPDATE_COURSE_LEVEL = `UPDATE_COURSE_LEVEL ${NAME}`;
export const UPDATE_COURSE_ID = `UPDATE_COURSE_ID ${NAME}`;
export const CLEAR_DELETED_LEVEL_REFERENCES = `CLEAR_DELETED_LEVEL_REFERENCES ${NAME}`;
export const CLEAR_DELETED_COURSE_REFERENCES = `CLEAR_DELETED_COURSE_REFERENCES ${NAME}`;

// Initial State
const initialState = {
  isEditMode: false,
  studentPersonalDetails: {},
  studentGradeRecords: {},
  deletedRecordRows: {},
  studentTranscriptComments: {},
  newCourseRows: {},
  newCourseRatings: {},
  updatedCourseLevels: {},
  updatedCourseCodes: {},
};

// Reducer Handlers
const REDUCER_HANDLERS = {
  [TOGGLE_EDIT_MODE]: state => {
    return update(state, {
      isEditMode: { $set: !state.isEditMode },
    });
  },

  [RESET_ACADEMIC_RECORDS_MODULE]: () => {
    return { ...initialState };
  },

  [UPDATE_STUDENT_PERSONAL_DETAILS]: (state, action) => {
    const { studentId, fieldUid, value } = action.payload;

    return update(state, {
      studentPersonalDetails: {
        [studentId]: {
          $apply: (currentDetails = {}) =>
            update(currentDetails, {
              [fieldUid]: { $set: value },
            }),
        },
      },
    });
  },

  [UPDATE_STUDENT_TRANSCRIPT_COMMENT]: (state, action) => {
    const { studentId, comment } = action.payload;

    return update(state, {
      studentTranscriptComments: {
        [studentId]: { $set: comment },
      },
    });
  },

  [DELETE_RECORD_ROW]: (state, action) => {
    const { studentId, gradeMapId, courseId } = action.payload;
    const compositeKey = getDeletedRowKey({ gradeMapId, courseId });

    return update(state, {
      deletedRecordRows: {
        [studentId]: {
          $apply: (currentDeletedRows = {}) =>
            update(currentDeletedRows, {
              [compositeKey]: { $set: true },
            }),
        },
      },
    });
  },

  [UPDATE_STUDENT_GRADE_RECORDS]: (state, action) => {
    const {
      studentId,
      gradeMapId,
      courseId,
      gradingPeriodId,
      criteriaId,
      value,
    } = action.payload;

    const compositeKey = getGradeRecordKey({
      gradeMapId,
      courseId,
      gradingPeriodId,
      criteriaId,
    });

    return update(state, {
      studentGradeRecords: {
        [studentId]: {
          $apply: (currentRecords = {}) =>
            update(currentRecords, {
              [compositeKey]: {
                $apply: (existingRecord = {}) => ({
                  ...existingRecord,
                  gradeMapId,
                  courseId,
                  gradingPeriodId,
                  criteriaId,
                  value,
                }),
              },
            }),
        },
      },
    });
  },

  [ADD_NEW_COURSE_ROW]: (state, action) => {
    const { studentId, gradeMapId, rowId } = action.payload;

    return update(state, {
      newCourseRows: {
        [studentId]: {
          $apply: (currentRows = {}) =>
            update(currentRows, {
              [gradeMapId]: {
                $apply: (gradeMapRows = {}) =>
                  update(gradeMapRows, {
                    [rowId]: {
                      $set: {
                        courseId: null,
                        levelId: null,
                      },
                    },
                  }),
              },
            }),
        },
      },
    });
  },

  [UPDATE_NEW_COURSE_ROW]: (state, action) => {
    const { studentId, gradeMapId, rowId, field, value } = action.payload;

    return update(state, {
      newCourseRows: {
        [studentId]: {
          [gradeMapId]: {
            [rowId]: {
              [field]: { $set: value },
            },
          },
        },
      },
    });
  },

  [DELETE_NEW_COURSE_ROW]: (state, action) => {
    const { studentId, gradeMapId, rowId } = action.payload;

    return update(state, {
      newCourseRows: {
        [studentId]: {
          [gradeMapId]: {
            $unset: [rowId],
          },
        },
      },
    });
  },

  [UPDATE_NEW_COURSE_RATING]: (state, action) => {
    const {
      studentId,
      rowId,
      gradingPeriodId,
      criteriaId,
      value,
    } = action.payload;

    const compositeKey = getNewCourseRatingKey({
      rowId,
      gradingPeriodId,
      criteriaId,
    });

    return update(state, {
      newCourseRatings: {
        [studentId]: {
          $apply: (currentRatings = {}) =>
            update(currentRatings, {
              [compositeKey]: { $set: value },
            }),
        },
      },
    });
  },

  [UPDATE_COURSE_LEVEL]: (state, action) => {
    const { studentId, gradeMapId, courseId, levelId } = action.payload;
    const compositeKey = getCourseLevelKey({ gradeMapId, courseId });

    return update(state, {
      updatedCourseLevels: {
        [studentId]: {
          $apply: (currentLevels = {}) =>
            update(currentLevels, {
              [compositeKey]: { $set: levelId },
            }),
        },
      },
    });
  },

  [UPDATE_COURSE_ID]: (state, action) => {
    const { studentId, gradeMapId, courseId, value } = action.payload;
    const compositeKey = getCourseCodeKey({ gradeMapId, courseId });

    return update(state, {
      updatedCourseCodes: {
        [studentId]: {
          $apply: (currentCourseCodes = {}) =>
            update(currentCourseCodes, {
              [compositeKey]: { $set: value },
            }),
        },
      },
    });
  },

  [CLEAR_DELETED_LEVEL_REFERENCES]: (state, action) => {
    const { levelIds = [] } = action.payload;

    if (_.isEmpty(levelIds)) return state;

    const levelIdSet = new Set(levelIds);

    const nextUpdatedCourseLevels = _.mapValues(
      state.updatedCourseLevels,
      studentLevels =>
        _.mapValues(studentLevels, levelId =>
          levelIdSet.has(levelId) ? null : levelId
        )
    );

    const nextNewCourseRows = _.mapValues(state.newCourseRows, studentRows =>
      _.mapValues(studentRows, gradeMapRows =>
        _.mapValues(gradeMapRows, rowData =>
          levelIdSet.has(rowData?.levelId)
            ? { ...rowData, levelId: null }
            : rowData
        )
      )
    );

    return update(state, {
      updatedCourseLevels: { $set: nextUpdatedCourseLevels },
      newCourseRows: { $set: nextNewCourseRows },
    });
  },

  [CLEAR_DELETED_COURSE_REFERENCES]: (state, action) => {
    const { courseIds = [] } = action.payload;

    if (_.isEmpty(courseIds)) return state;

    const courseIdSet = new Set(courseIds);

    const nextNewCourseRows = _.mapValues(state.newCourseRows, studentRows =>
      _.mapValues(studentRows, gradeMapRows =>
        _.mapValues(gradeMapRows, rowData =>
          courseIdSet.has(rowData?.courseId)
            ? { ...rowData, courseId: null, levelId: null, courseCode: null }
            : rowData
        )
      )
    );

    return update(state, {
      newCourseRows: { $set: nextNewCourseRows },
    });
  },
};

function academicRecordsReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

export default academicRecordsReducer;
